import { useState } from 'react';
import { motion } from 'framer-motion';
import { Edit, Plus, Save, Trash2 } from 'lucide-react';

const SupplierModule = ({ suppliers, onAdd, onEdit, onDelete, loading, readOnly }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', website: '', address: '', city: '', state: '', zip: '', country: 'Indonesia', contactPerson: '', notes: ''
  });

  const resetForm = () => {
    setFormData({ name: '', phone: '', email: '', website: '', address: '', city: '', state: '', zip: '', country: 'Indonesia', contactPerson: '', notes: '' });
    setEditingSupplier(null);
    setShowForm(false);
  };

  const handleEdit = (supplier) => {
    setFormData({ ...supplier });
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return alert('Nama supplier wajib diisi');
    if (editingSupplier) {
      onEdit({ ...formData, id: editingSupplier.id });
    } else {
      onAdd(formData);
    }
    resetForm();
  };

  const filtered = suppliers.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q) || s.contactPerson?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-heading font-bold tracking-tight">Supplier</h2>
          <p className="text-muted-foreground mt-1">Kelola data supplier Anda</p>
        </div>
        {!readOnly && (
          <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary" data-testid="add-supplier-btn">
            <Plus size={18}/> Tambah Supplier
          </button>
        )}
      </div>

      {showForm && !readOnly && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="card p-6">
          <h3 className="font-heading font-semibold text-lg mb-4">{editingSupplier ? 'Edit Supplier' : 'Supplier Baru'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Nama Supplier<span className="text-destructive">*</span></label>
              <input className="input-field" placeholder="Nama supplier..." value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} data-testid="supplier-name-input" />
            </div>
            <div>
              <label className="label">Contact Person</label>
              <input className="input-field" placeholder="Nama kontak..." value={formData.contactPerson} onChange={e => setFormData({...formData, contactPerson: e.target.value})} data-testid="supplier-cp-input" />
            </div>
            <div>
              <label className="label">Telepon</label>
              <input className="input-field" placeholder="Nomor telepon..." value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} data-testid="supplier-phone-input" />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input-field" type="email" placeholder="email@supplier.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} data-testid="supplier-email-input" />
            </div>
            <div>
              <label className="label">Website</label>
              <input className="input-field" placeholder="www.supplier.com" value={formData.website} onChange={e => setFormData({...formData, website: e.target.value})} />
            </div>
            <div>
              <label className="label">Kota</label>
              <input className="input-field" placeholder="Kota..." value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} data-testid="supplier-city-input" />
            </div>
            <div className="md:col-span-2">
              <label className="label">Alamat</label>
              <textarea className="input-field" rows={2} placeholder="Alamat lengkap..." value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} data-testid="supplier-address-input" />
            </div>
            <div className="md:col-span-2">
              <label className="label">Catatan</label>
              <textarea className="input-field" rows={2} placeholder="Catatan tentang supplier..." value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={resetForm} className="btn-secondary">Batal</button>
            <button onClick={handleSubmit} className="btn-primary" data-testid="save-supplier-btn">
              <Save size={16}/> {editingSupplier ? 'Simpan Perubahan' : 'Tambah Supplier'}
            </button>
          </div>
        </motion.div>
      )}

      {/* Search */}
      <div className="card p-4">
        <div className="relative">
          <input className="input-field pl-10" placeholder="Cari supplier..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="supplier-search" />
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                <th className="px-6 py-4 text-left">No</th>
                <th className="px-6 py-4 text-left">Nama</th>
                <th className="px-6 py-4 text-left">Contact Person</th>
                <th className="px-6 py-4 text-left">Telepon</th>
                <th className="px-6 py-4 text-left">Email</th>
                <th className="px-6 py-4 text-left">Kota</th>
                {!readOnly && <th className="px-6 py-4 text-right">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={readOnly ? "6" : "7"} className="px-6 py-12 text-center text-muted-foreground">Memuat data...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={readOnly ? "6" : "7"} className="px-6 py-12 text-center text-muted-foreground">{searchQuery ? 'Tidak ada hasil ditemukan.' : 'Belum ada supplier.'}</td></tr>
              ) : filtered.map((s, index) => (
                <tr key={s.id} className="table-row">
                  <td className="px-6 py-4 text-muted-foreground">{index + 1}</td>
                  <td className="px-6 py-4 font-medium">{s.name}</td>
                  <td className="px-6 py-4">{s.contactPerson || '-'}</td>
                  <td className="px-6 py-4">{s.phone || '-'}</td>
                  <td className="px-6 py-4">{s.email || '-'}</td>
                  <td className="px-6 py-4">{s.city || '-'}</td>
                  {!readOnly && (
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handleEdit(s)} className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-primary transition-colors" data-testid={`edit-supplier-${s.id}`}><Edit size={16}/></button>
                        <button onClick={() => onDelete(s.id)} className="p-2 hover:bg-destructive/10 rounded-lg text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-supplier-${s.id}`}><Trash2 size={16}/></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== PURCHASE ORDER MODULE ==============


export default SupplierModule;
